// User Role Types and Permissions System

export type UserRole = 'admin' | 'manager' | 'operator' | 'viewer';

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: 'aws' | 'campaigns' | 'domains' | 'email' | 'analytics' | 'users' | 'settings';
}

export interface RolePermissions {
  role: UserRole;
  name: string;
  description: string;
  permissions: Permission[];
  color: string;
  icon: string;
}

// Define all available permissions
export const ALL_PERMISSIONS: Permission[] = [
  // AWS Management
  { id: 'aws.create_account', name: 'Create AWS Account', description: 'Create new AWS accounts', category: 'aws' },
  { id: 'aws.delete_account', name: 'Delete AWS Account', description: 'Delete AWS accounts', category: 'aws' },
  { id: 'aws.view_accounts', name: 'View AWS Accounts', description: 'View all AWS accounts', category: 'aws' },
  { id: 'aws.test_connection', name: 'Test AWS Connection', description: 'Test AWS account connections', category: 'aws' },
  
  // Campaign Management
  { id: 'campaigns.create', name: 'Create Campaigns', description: 'Create new email campaigns', category: 'campaigns' },
  { id: 'campaigns.edit', name: 'Edit Campaigns', description: 'Edit existing campaigns', category: 'campaigns' },
  { id: 'campaigns.delete', name: 'Delete Campaigns', description: 'Delete campaigns', category: 'campaigns' },
  { id: 'campaigns.send', name: 'Send Campaigns', description: 'Send email campaigns', category: 'campaigns' },
  { id: 'campaigns.view', name: 'View Campaigns', description: 'View all campaigns', category: 'campaigns' },
  
  // Domain Management
  { id: 'domains.add', name: 'Add Domains', description: 'Add new domains', category: 'domains' },
  { id: 'domains.verify', name: 'Verify Domains', description: 'Verify domain ownership', category: 'domains' },
  { id: 'domains.delete', name: 'Delete Domains', description: 'Delete domains', category: 'domains' },
  { id: 'domains.view', name: 'View Domains', description: 'View all domains', category: 'domains' },
  
  // Email Account Management
  { id: 'email.create_account', name: 'Create Email Accounts', description: 'Create new email accounts', category: 'email' },
  { id: 'email.delete_account', name: 'Delete Email Accounts', description: 'Delete email accounts', category: 'email' },
  { id: 'email.view_accounts', name: 'View Email Accounts', description: 'View all email accounts', category: 'email' },
  { id: 'email.rotation', name: 'Manage Email Rotation', description: 'Configure email rotation schedules', category: 'email' },
  
  // Analytics
  { id: 'analytics.view', name: 'View Analytics', description: 'View campaign analytics and reports', category: 'analytics' },
  { id: 'analytics.export', name: 'Export Analytics', description: 'Export analytics data', category: 'analytics' },
  
  // User Management
  { id: 'users.create', name: 'Create Users', description: 'Create new user accounts', category: 'users' },
  { id: 'users.edit', name: 'Edit Users', description: 'Edit user accounts', category: 'users' },
  { id: 'users.delete', name: 'Delete Users', description: 'Delete user accounts', category: 'users' },
  { id: 'users.view', name: 'View Users', description: 'View all users', category: 'users' },
  { id: 'users.assign_roles', name: 'Assign Roles', description: 'Assign roles to users', category: 'users' },
  
  // Settings
  { id: 'settings.general', name: 'General Settings', description: 'Modify general application settings', category: 'settings' },
  { id: 'settings.integrations', name: 'Integration Settings', description: 'Configure third-party integrations', category: 'settings' },
  { id: 'settings.billing', name: 'Billing Settings', description: 'Manage billing and subscriptions', category: 'settings' },
];

// Define role permissions
export const ROLE_PERMISSIONS: RolePermissions[] = [
  {
    role: 'admin',
    name: 'Administrator',
    description: 'Full access to all features and settings',
    color: 'danger',
    icon: 'ShieldFill',
    permissions: ALL_PERMISSIONS, // Admin has all permissions
  },
  {
    role: 'manager',
    name: 'Manager',
    description: 'Manage campaigns, domains, and email accounts',
    color: 'warning',
    icon: 'PersonGear',
    permissions: ALL_PERMISSIONS.filter(p => 
      !p.id.startsWith('users.') && 
      !p.id.startsWith('settings.')
    ),
  },
  {
    role: 'operator',
    name: 'Operator',
    description: 'Operate campaigns and manage email accounts',
    color: 'info',
    icon: 'PersonCheck',
    permissions: ALL_PERMISSIONS.filter(p => 
      p.id.startsWith('campaigns.') ||
      p.id.startsWith('email.') ||
      p.id.startsWith('analytics.view') ||
      p.id.startsWith('domains.view')
    ),
  },
  {
    role: 'viewer',
    name: 'Viewer',
    description: 'View-only access to campaigns and analytics',
    color: 'secondary',
    icon: 'Eye',
    permissions: ALL_PERMISSIONS.filter(p => 
      p.id.includes('view') || 
      p.id === 'analytics.view'
    ),
  },
];

// Helper functions
export const getUserRolePermissions = (role: UserRole): Permission[] => {
  const roleConfig = ROLE_PERMISSIONS.find(r => r.role === role);
  return roleConfig ? roleConfig.permissions : [];
};

export const hasPermission = (userRole: UserRole, permissionId: string): boolean => {
  const permissions = getUserRolePermissions(userRole);
  return permissions.some(p => p.id === permissionId);
};

export const getRoleConfig = (role: UserRole): RolePermissions | undefined => {
  return ROLE_PERMISSIONS.find(r => r.role === role);
};

export const getPermissionCategories = (): string[] => {
  const categories = new Set(ALL_PERMISSIONS.map(p => p.category));
  return Array.from(categories);
};
